// Dev-only agent-template fixtures + their dispatcher, split out of `mocks.ts` on
// the `dashboard-mocks` precedent. Absent entirely until now, so
// `/org/:org/account/templates` rendered `<ErrorState/>` under `PUBLIC_MOCKS`.
//
// THE ADDRESS IS THE NAME, not an id (`PUT|DELETE /v1/templates/:name`), and a
// name may carry spaces or a slash — so this dispatcher decodes the segment
// rather than matching a `tpl_` id, which is what a mock keyed on the wrong
// segment would have started diverging on the first time somebody named a
// template "Claude (fast)".
//
// STATEFUL, because a PUT is a FULL REPLACE and the editor re-READS after every
// save. A stateless echo would re-serve the pre-save row and make the "an omitted
// field resets to its default" rule — the whole reason the form sends every
// column — unobservable in the story.

import type { ApiTemplate } from './agent-templates';
import { at } from './mock-session';
import { unhandledMockRoute } from './unhandled-mock-route';

/**
 * One paired backend (needs `base_url`) and one unpaired, so the form's hint and
 * the server's 422 pairing rule both have a row to sit against. `invocation` is
 * populated on exactly one: it is the field a UI edit silently wipes if the form
 * omits it, and a fixture where it is null everywhere cannot show that.
 */
const templates: ApiTemplate[] = [
  {
    id: 'tpl_mock01',
    name: 'claude',
    program: 'claude',
    invocation: { command: 'claude', args: ['-p'], promptVia: 'stdin' },
    backend: 'anthropic',
    base_url: null,
    model: 'claude-sonnet-4-6',
    system_prompt: 'Ship small, reviewable diffs. Never push to main.',
    kb_ref: 'repo',
    created_iso: at('2026-06-02'),
    updated_iso: at('2026-07-11'),
  },
  {
    id: 'tpl_mock02',
    name: 'glm-fallback',
    program: 'custom',
    invocation: null,
    backend: 'openai-compatible',
    base_url: 'https://open.bigmodel.cn/api/paas/v4',
    model: 'glm-5',
    system_prompt: null,
    kb_ref: null,
    created_iso: at('2026-06-20'),
    updated_iso: at('2026-06-20'),
  },
];

/**
 * Handle a `/v1/templates*` request, or return `null` if `path` is not a template
 * route (so `mockFetch` falls through). A PUT on an unknown name CREATES — the
 * route is an upsert keyed by name — while a DELETE is idempotent, which is why
 * neither can reject on a name nobody holds.
 */
export function templatesMock(
  path: string,
  method: string,
  init?: RequestInit,
): Promise<unknown> | null {
  const bare = path.split('?')[0] ?? path;
  if (bare !== '/v1/templates' && !bare.startsWith('/v1/templates/')) return null;
  const reject = (): Promise<never> => unhandledMockRoute(method, path);

  if (bare === '/v1/templates') {
    // `next_cursor` is a permanent, TRUTHFUL null here: the route serves the whole
    // collection on purpose, so this is the completeness assertion, not a stub.
    return method === 'GET' ? Promise.resolve({ data: templates, next_cursor: null }) : reject();
  }

  const name = decodeURIComponent(bare.slice('/v1/templates/'.length));
  const i = templates.findIndex((t) => t.name === name);
  if (method === 'GET') {
    const row = templates[i];
    return row === undefined ? reject() : Promise.resolve({ template: row });
  }
  if (method === 'PUT') return Promise.resolve({ template: replace(name, i, init) });
  if (method === 'DELETE') {
    if (i >= 0) templates.splice(i, 1);
    // 204 with an EMPTY body, whether or not the row existed.
    return Promise.resolve(undefined);
  }
  return reject();
}

/**
 * The REPLACE. Every mutable column comes from the body or falls to its column
 * default — deliberately NOT to the row's previous value, because that is what
 * makes an omitted field a silent reset, and a mock that merged instead would
 * hide the exact defect the "send every field" rule exists to prevent.
 */
function replace(name: string, i: number, init?: RequestInit): ApiTemplate {
  const body = init?.body ? (JSON.parse(String(init.body)) as Record<string, unknown>) : {};
  const str = (key: string): string | null =>
    typeof body[key] === 'string' ? (body[key] as string) : null;
  const row: ApiTemplate = {
    id: templates[i]?.id ?? `tpl_new_${templates.length + 1}`,
    name,
    program: str('program') ?? 'custom',
    invocation: body['invocation'] ?? null,
    backend: str('backend') ?? 'none',
    base_url: str('base_url'),
    model: str('model'),
    system_prompt: str('system_prompt'),
    kb_ref: str('kb_ref'),
    created_iso: templates[i]?.created_iso ?? at('2026-07-20'),
    updated_iso: at('2026-07-20'),
  };
  if (i >= 0) templates[i] = row;
  else templates.push(row);
  return row;
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
const PRISTINE_TEMPLATES = templates.map((row) => structuredClone(row));

export function resetTemplatesMocks(): void {
  templates.splice(0, templates.length, ...PRISTINE_TEMPLATES.map((row) => structuredClone(row)));
}
